import { useEffect, useState } from 'react';
import { FaChevronDown, FaChevronUp, FaClock, FaEnvelope, FaMapMarkerAlt, FaPhone, FaWhatsapp } from 'react-icons/fa';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate } from 'react-router-dom';
import './ContactUs.css';

const ContactUs = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
    });

    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Name is required';

        // Make email optional but validate format if provided
        if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email address is invalid';
        }

        // Validate phone - using library's format so just check if it exists
        if (!formData.phone) {
            errors.phone = 'Phone number is required';
        } else if (formData.phone.length < 8) {
            errors.phone = 'Please enter a valid phone number';
        }

        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error for this field when user starts typing
        if (formErrors[name]) {
            setFormErrors({
                ...formErrors,
                [name]: null,
            });
        }
    };

    const handlePhoneChange = (value, country, e, formattedValue) => {
        setFormData({
            ...formData,
            phone: value,
        });

        // Clear error when user changes phone
        if (formErrors.phone) {
            setFormErrors({
                ...formErrors,
                phone: null,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validateForm();

        if (Object.keys(errors).length === 0) {
            setIsSubmitting(true);

            try {
                // Clear any previous submission tracking
                const submissionKey = `submission-${formData.name}-${formData.phone}`;
                const whatsappKey = `whatsapp-${formData.name}-${formData.phone}`;

                // Clear previous tracking for this user if it exists
                sessionStorage.removeItem(submissionKey);
                sessionStorage.removeItem(whatsappKey);

                // Fire GTM event for form submission start
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'form_submission_start',
                        formType: 'contact',
                    });
                }

                // Create a request to Google Apps Script
                const scriptURL =
                    'https://script.google.com/macros/s/AKfycbwNJdPuF7LgDL4fJYMPdZG7bZoyvzvVqc50iyuZL6P8q0HKyI20mrQgyyA8SS_NchC-hg/exec'; // Replace with your script URL

                // Send the form data as JSON
                const response = await fetch(scriptURL, {
                    method: 'POST',
                    body: JSON.stringify(formData),
                    headers: {
                        'Content-Type': 'text/plain;charset=utf-8',
                    },
                    mode: 'no-cors', // Important for handling CORS issues
                });

                // Navigate to thank you page with all form data
                navigate('/thank-you', {
                    state: {
                        name: formData.name,
                        email: formData.email,
                        phone: formData.phone,
                        message: formData.message,
                    },
                });
            } catch (error) {
                console.error('Error submitting form:', error);

                // Fire GTM event for form submission error
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: 'form_submission_error',
                        errorMessage: error.message || 'Unknown error occurred',
                    });
                }

                alert('There was an error submitting your message. Please try again later.');
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setFormErrors(errors);

            // Fire GTM event for form validation error
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'form_validation_error',
                    formErrors: Object.keys(errors),
                    formType: 'contact',
                });
            }
        }
    };

    // Rest of your component code remains the same...

    const faqItems = [
        {
            question: 'How do I schedule my first appointment?',
            answer: 'You can schedule your first appointment by filling out our contact form, calling us directly at +91-8076353837, or emailing us at contactwisdomdentalclinic@gmail.com.',
        },
        {
            question: 'Do you accept insurance?',
            answer: "Yes, we accept most major dental insurance plans. Please contact us with your insurance details, and we'll verify your coverage before your appointment.",
        },
    ];

    const [expandedFaq, setExpandedFaq] = useState(null);

    const toggleFaq = (index) => {
        if (expandedFaq === index) {
            setExpandedFaq(null);
        } else {
            setExpandedFaq(index);
        }
    };

    return (
        <div className='improved-contact-container'>
            {/* Hero Section */}
            <div className='improved-hero'>
                <h1>Get in Touch with Wisdom Dental Clinic</h1>
            </div>

            {/* Main Contact Section */}
            <div className='improved-contact-grid'>
                {/* Form Section */}
                <div className='improved-form-container'>
                    <h2>Send Us a Message</h2>
                    <p className='improved-response-time'>We'll respond within 15 minutes during business hours</p>

                    <form onSubmit={handleSubmit} className='improved-contact-form'>
                        <div className='improved-form-group'>
                            <label htmlFor='name'>
                                Full Name <span className='required'>*</span>
                            </label>
                            <input
                                type='text'
                                id='name'
                                name='name'
                                value={formData.name}
                                onChange={handleChange}
                                className={formErrors.name ? 'error' : ''}
                                placeholder='Your full name'
                            />
                            {formErrors.name && <span className='error-message'>{formErrors.name}</span>}
                        </div>

                        <div className='improved-form-row'>
                            <div className='improved-form-group'>
                                <label htmlFor='phone'>
                                    Phone Number <span className='required'>*</span>
                                </label>
                                <PhoneInput
                                    country={'in'}
                                    value={formData.phone}
                                    onChange={handlePhoneChange}
                                    inputProps={{
                                        id: 'phone',
                                        name: 'phone',
                                        required: true,
                                        placeholder: 'Enter phone number',
                                        autoComplete: 'tel',
                                    }}
                                    containerClass={`improved-phone-input-container ${formErrors.phone ? 'has-error' : ''}`}
                                    buttonClass='country-dropdown'
                                    inputClass={formErrors.phone ? 'phone-input-error' : 'phone-input-normal'}
                                    dropdownClass='country-dropdown-list'
                                    preferredCountries={['in', 'us', 'gb', 'ae', 'sg']}
                                    enableSearch={true}
                                    searchPlaceholder='Search countries...'
                                    countryCodeEditable={false}
                                    disableDropdown={false}
                                    autoFormat={true}
                                    specialLabel=''
                                />
                                {formErrors.phone && <span className='error-message'>{formErrors.phone}</span>}
                            </div>

                            <div className='improved-form-group'>
                                <label htmlFor='email'>Email Address (Optional)</label>
                                <input
                                    type='email'
                                    id='email'
                                    name='email'
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={formErrors.email ? 'error' : ''}
                                    placeholder='your.email@example.com (optional)'
                                />
                                {formErrors.email && <span className='error-message'>{formErrors.email}</span>}
                            </div>
                        </div>

                        <div className='improved-form-group'>
                            <label htmlFor='message'>Message (Optional)</label>
                            <textarea
                                id='message'
                                name='message'
                                value={formData.message}
                                onChange={handleChange}
                                rows='3'
                                placeholder='Tell us about your dental concerns or ask any questions...'
                            ></textarea>
                        </div>

                        <button type='submit' className='improved-submit-button' disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                </div>

                {/* The rest of your component code... */}
                <div className='improved-contact-info'>
                    <h2>Contact Information</h2>
                    <div
                        className='improved-contact-option'
                        onClick={() => (window.location.href = 'tel:+918076353837')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className='improved-contact-icon'>
                            <FaPhone />
                        </div>
                        <div className='improved-contact-details'>
                            <h3>Call Us</h3>
                            <span>+91-8076353837</span>
                        </div>
                    </div>
                    <div
                        className='improved-contact-option'
                        onClick={() => window.open('https://wa.me/918076353837', '_blank', 'noreferrer')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className='improved-contact-icon'>
                            <FaWhatsapp />
                        </div>
                        <div className='improved-contact-details'>
                            <h3>Chat with Us</h3>
                            <span>+91-8076353837</span>
                        </div>
                    </div>
                    <div
                        className='improved-contact-option'
                        onClick={() => (window.location.href = 'mailto:contactwisdomdentalclinic@gmail.com')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className='improved-contact-icon'>
                            <FaEnvelope />
                        </div>
                        <div className='improved-contact-details'>
                            <h3>Email Us</h3>
                            <span>contactwisdomdentalclinic@gmail.com</span>
                        </div>
                    </div>
                    <div
                        className='improved-contact-option'
                        onClick={() => window.open('https://maps.app.goo.gl/FHrdhMCpvA2wHJ3U8', '_blank', 'noreferrer')}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className='improved-contact-icon'>
                            <FaMapMarkerAlt />
                        </div>
                        <div className='improved-contact-details'>
                            <h3>Visit Us</h3>
                            <address>120, Huda Market Sector 23, Gurugram, Haryana</address>
                        </div>
                    </div>
                    <div className='improved-contact-option'>
                        <div className='improved-contact-icon'>
                            <FaClock />
                        </div>
                        <div className='improved-contact-details'>
                            <h3>Opening Hours</h3>
                            <p>Everyday: 9:00 AM – 8:00 PM</p>
                        </div>
                    </div>
                    {/* Map Section */}
                    <div className='improved-map-container'>
                        <iframe
                            src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d112158.89728080697!2d76.97840153940868!3d28.540755479818788!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d19c6a89a346d%3A0xd244c95d65246335!2sWisdom%20Dental%20and%20Implant%20Clinic%20%7C%20Sector%2023!5e0!3m2!1sen!2sin!4v1755105831821!5m2!1sen!2sin'
                            width='600'
                            height='450'
                            style={{ border: 0 }}
                            allowFullScreen=''
                            loading='lazy'
                        ></iframe>
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className='improved-faq-section'>
                <h2>Frequently Asked Questions</h2>
                <div className='improved-faq-list'>
                    {faqItems.map((item, index) => (
                        <div key={index} className={`improved-faq-item ${expandedFaq === index ? 'expanded' : ''}`}>
                            <div className='improved-faq-question' onClick={() => toggleFaq(index)}>
                                <h3>{item.question}</h3>
                                <span className='improved-faq-toggle'>{expandedFaq === index ? <FaChevronUp /> : <FaChevronDown />}</span>
                            </div>
                            {expandedFaq === index && (
                                <div className='improved-faq-answer'>
                                    <p>{item.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ContactUs;
